/* eslint-disable @next/next/no-img-element */

import React from 'react';
import styled from './theme/styled.js';
import { includes } from './utils/fp-utils.js';
import { token } from './theme/tokens.js';
import useWindowObjectObserver from '../../app/hooks/useWindowObjectObserver.js';
import amazonSmallLogo from '../../public/images/amazon-small-logo.svg';
import externalLinkIcon from '../../public/images/external-link-icon.svg';
import bwpLogo from '../../public/images/amazon-buy-with-prime.svg';
import useCartState from './hooks/useCartState.js';
import { getCountryByCurrencyCode } from '../../app/cart/countries.js';
import { toLower } from 'ramda';
import { isAmazonPrimeBigDealDaysActiveEffective } from './utils/amazon.js';
import AmazonLogoWhite from './elements/AmazonLogoWhite.js';

const GTM_CLASS_NAME = 'trigger-gtm-amazon-order-button trigger-gtm-pricing-page-amazon-order-button';
const GTM_CLASS_NAME_BWP = 'trigger-gtm-amazon-buy-with-prime-button';

const buildAmazonUrl = (config, isBWPEnabled, isCanada) => {
    if (isBWPEnabled && !isCanada) {
        return '/buy-with-prime';
    }
    const tld = isCanada ? 'ca' : 'com';
    const pathname = isCanada ? 'dp/B07H8NS5MS' : 'bouncie';
    const maasKey = config.amazon?.maasKey;
    const queryString = maasKey ? `?maas=${maasKey}&ref_=aa_maas&tag=maas` : '';

    return `https://www.amazon.${tld}/${pathname}${queryString}`;
};

function AmazonLinkUnstyled({ isBWPEnabled, countryCode, className, config, onClick, children }) {
    const countryCodeDefaulted = includes(countryCode, ['us', 'ca']) ? countryCode : 'us';
    const isCanada = countryCodeDefaulted === 'ca';

    const href = buildAmazonUrl(config, isBWPEnabled, isCanada);

    return <a href={href} target={href.startsWith('http') ? '_blank' : '_self'} className={className} onClick={onClick}>
        {children}
    </a>;
}

const LinkWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    paddingTop: 18,
    borderTop: `1px solid ${token(theme, 'color.borderAmazon')}`,
}));

const AmazonLink = styled(AmazonLinkUnstyled)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    fontSize: token(theme, 'fontSize.body'),
    gap: 10,
    color: token(theme, 'color.accent'),
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline',
    },
}));

const AmazonLogoWrapper = styled.div({
    width: 23,
    paddingTop: 5,
});

const ExternalLinkIconWrapper = styled.div({
    width: 18,
    paddingTop: 2,
});

const BuyWithPrimeImage = styled.img([{
    height: 'fit-content',
    maxWidth: 'unset',
    width: 50,
}]);

const PrimeDayStyledLink = styled.a(({ theme }) => ({
    background: token(theme, 'color.surfaceInverse'),
    borderRadius: token(theme, 'radius.button'),
    width: '100%',
    color: token(theme, 'color.onAccent'),
    fontSize: token(theme, 'fontSize.body'),
    gap: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'none',
    },
}));

export function PrimeDayOrderOnAmazonLink({ href, onClick }) {
    return <LinkWrapper>
        <PrimeDayStyledLink href={href} target="_blank">
            <span css={{ marginRight: 3 }}>Order on</span>
            <AmazonLogoWhite styles={{ marginTop: 20, marginBottom: 5 }}/>
        </PrimeDayStyledLink>
    </LinkWrapper>;
}

export function BigDealOrderOnAmazonLink({ href, onClick }) {
    return <LinkWrapper>
        <PrimeDayStyledLink href={href} target="_blank">
            <span css={{ marginRight: 3 }}>Order on</span>
            <AmazonLogoWhite styles={{ marginTop: 20, marginBottom: 5 }}/>
        </PrimeDayStyledLink>
    </LinkWrapper>;
}

// eslint-disable-next-line max-statements
export default function OrderOnAmazonLink({ config, pricing }) {
    const { currency, updateCartState } = useCartState();
    const countryCode = toLower(getCountryByCurrencyCode(currency).countryCode);
    const handleClick = () => updateCartState({ isCartPanelOpen: false });
    const isCanada = countryCode === 'ca';
    const isBwpCartInitialized = useWindowObjectObserver('bwpCart.isInitialized');
    const isBwpCartError = useWindowObjectObserver('bwpCart.isApiError');
    const isBwpCartReady = isBwpCartInitialized && !isBwpCartError;

    const isActiveBigDealDays = isAmazonPrimeBigDealDaysActiveEffective(
        config.amazon?.primeBigDealDays,
        countryCode,
    );

    const primeDayHref = isCanada ?
        config.amazon?.primeDay?.attributionLinkCanada :
        config.amazon?.primeDay?.attributionLinkUnitedStates;
    const primeBigDealHref = isCanada ?
        config.amazon?.primeBigDealDays?.attributionLinkCanada :
        config.amazon?.primeBigDealDays?.attributionLinkUnitedStates;

    if (config.amazon?.primeDay?.isActive) {
        return <PrimeDayOrderOnAmazonLink
            href={primeDayHref}
            onClick={handleClick}
        />;
    }
    if (isActiveBigDealDays) {
        return <BigDealOrderOnAmazonLink
            href={primeBigDealHref}
            onClick={handleClick}
        />;
    }

    const className = isBwpCartReady ? GTM_CLASS_NAME_BWP : GTM_CLASS_NAME;

    return <LinkWrapper>
        <AmazonLink countryCode={countryCode} className={className} isBWPEnabled={isBwpCartReady} config={config} onClick={handleClick}>
            {isBwpCartReady && countryCode === 'us' ?
                <>
                    <BuyWithPrimeImage src={bwpLogo} alt="Order from Amazon" /><span>Order from Amazon</span>
                </> :
                <>
                    <AmazonLogoWrapper>
                        <img src={amazonSmallLogo} alt="Amazon order now" layout="responsive" />
                    </AmazonLogoWrapper>
                    Order on Amazon
                    <ExternalLinkIconWrapper>
                        <img src={externalLinkIcon} alt="Amazon order now" layout="responsive" />
                    </ExternalLinkIconWrapper>
                </>
            }
        </AmazonLink>
    </LinkWrapper>;
}
